// The Timeline lab charts' colours: the Signal DS chart tokens (Okabe–Ito blue + orange, colour-blind safe) read from CSS
// variables, for ECharts, which takes colour strings. Shared by the trend cards and Compare results.
import { useSyncExternalStore } from 'react';
import type { CompareColors } from '@/patients/lib/lab-trend-cards';

/** Signal DS tokens. A host without the token sheet (tests) reads none, and ECharts uses its own default. */
const TOKENS = {
  first: '--chart-blue',
  second: '--chart-orange',
  axis: '--color-border-default',
  grid: '--color-border-subtle',
  text: '--color-text-muted',
  band: '--color-viz-reference-band',
} as const;

export function readTokenColors(): CompareColors {
  const style = typeof document !== 'undefined' ? getComputedStyle(document.documentElement) : null;
  const read = (name: string) => style?.getPropertyValue(name).trim() || undefined;
  return {
    series: [read(TOKENS.first), read(TOKENS.second)],
    axis: read(TOKENS.axis),
    grid: read(TOKENS.grid),
    text: read(TOKENS.text),
    band: read(TOKENS.band),
  };
}

const sameColors = (a: CompareColors, b: CompareColors) =>
  a.series[0] === b.series[0] &&
  a.series[1] === b.series[1] &&
  a.axis === b.axis &&
  a.grid === b.grid &&
  a.text === b.text &&
  a.band === b.band;

// One shared reader for every chart on the page: one observer and one media listener, started by the first chart and
// stopped by the last. Listeners get a new object only when a colour really changed (popovers often touch <html> style).
let current: CompareColors | null = null;
const listeners = new Set<() => void>();
let stop: (() => void) | null = null;

function reread() {
  const next = readTokenColors();
  if (current && sameColors(current, next)) return;
  current = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!stop && typeof document !== 'undefined') {
    const media = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    media?.addEventListener?.('change', reread);
    const observer = typeof MutationObserver === 'function' ? new MutationObserver(reread) : null;
    observer?.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme', 'style'] });
    stop = () => {
      media?.removeEventListener?.('change', reread);
      observer?.disconnect();
    };
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && stop) {
      stop();
      stop = null;
      current = null;
    }
  };
}

function snapshot(): CompareColors {
  if (!current) current = readTokenColors();
  return current;
}

/**
 * The token colours, read again when the theme changes: the OS colour scheme, or a class / data-theme / style change on
 * <html> (where a theme switch sets the tokens). ECharts takes colour strings, so a CSS variable cannot follow by itself.
 */
export function useTokenColors(): CompareColors {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
